import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

@customElement('app-slider')
export class AppSlider extends LitElement {
  @property({ type: Number }) value = 0;
  @property({ type: Number }) min = 0;
  @property({ type: Number }) max = 1;
  @property({ type: Number }) step = 0.01;
  @property({ type: String }) backgroundColor = 'rgba(255, 255, 255, 0.2)';
  @property({ type: String }) foregroundColor = '#ffffff';
  @property({ type: Number }) height = 40;
  
  @state() private isDragging = false;
  @state() private internalValue = 0;

  static styles = css`
    :host {
      display: inline-block;
      width: 100%;
      min-width: 0;
      box-sizing: border-box;
    }
    
    .slider-container {
      position: relative;
      cursor: pointer;
      user-select: none;
      -webkit-user-select: none;
      -webkit-touch-callout: none;
      -webkit-tap-highlight-color: transparent;
    }
    
    .slider-track {
      position: relative;
      width: 100%;
      overflow: hidden;
    }
    
    .slider-fill {
      height: 100%;
      transition: width 0.1s ease;
      pointer-events: none;
    }
  `;

  protected createRenderRoot() {
    return this; // Use light DOM instead of shadow DOM
  }

  protected firstUpdated(): void {
    this.internalValue = this.value;
    this.setupEventListeners();
  }

  protected updated(changedProperties: Map<string, any>): void {
    if (changedProperties.has('value')) {
      this.internalValue = this.value;
    }
  }

  private setupEventListeners(): void {
    const container = this.querySelector('.slider-container') as HTMLElement;
    if (!container) return;

    // Mouse events
    container.addEventListener('mousedown', this.handlePointerStart.bind(this));
    document.addEventListener('mousemove', this.handlePointerMove.bind(this));
    document.addEventListener('mouseup', this.handlePointerEnd.bind(this));

    // Touch events for iOS compatibility
    container.addEventListener('touchstart', this.handlePointerStart.bind(this), { passive: false });
    document.addEventListener('touchmove', this.handlePointerMove.bind(this), { passive: false });
    document.addEventListener('touchend', this.handlePointerEnd.bind(this));
    document.addEventListener('touchcancel', this.handlePointerEnd.bind(this));
  }

  private handlePointerStart(e: MouseEvent | TouchEvent): void {
    e.preventDefault();
    this.isDragging = true;
    this.updateValueFromEvent(e);
  }

  private handlePointerMove(e: MouseEvent | TouchEvent): void {
    if (!this.isDragging) return;
    e.preventDefault();
    this.updateValueFromEvent(e);
  }

  private handlePointerEnd(e: MouseEvent | TouchEvent): void {
    if (!this.isDragging) return;
    e.preventDefault();
    this.isDragging = false;
  }

  private updateValueFromEvent(e: MouseEvent | TouchEvent): void {
    const container = this.querySelector('.slider-container') as HTMLElement;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    let clientX: number;

    if (e instanceof MouseEvent) {
      clientX = e.clientX;
    } else {
      clientX = e.touches[0]?.clientX || e.changedTouches[0]?.clientX || 0;
    }

    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    
    const newValue = this.min + percentage * (this.max - this.min);
    const steppedValue = Math.round(newValue / this.step) * this.step;
    
    this.internalValue = Math.max(this.min, Math.min(this.max, steppedValue));
    this.value = this.internalValue;
    
    this.dispatchEvent(new CustomEvent('input', {
      detail: { value: this.value },
      bubbles: true
    }));
    
    this.dispatchEvent(new CustomEvent('change', {
      detail: { value: this.value },
      bubbles: true
    }));
  }

  private get fillPercentage(): number {
    const range = this.max - this.min;
    return range > 0 ? ((this.internalValue - this.min) / range) * 100 : 0;
  }

  render() {
    return html`
      <div 
        class="slider-container"
        style="height: ${this.height}px;"
      >
        <div 
          class="slider-track"
          style="
            height: ${this.height}px;
            background-color: ${this.backgroundColor};
          "
        >
          <div 
            class="slider-fill"
            style="
              height: ${this.height}px;
              width: ${this.fillPercentage}%;
              background-color: ${this.foregroundColor};
            "
          ></div>
        </div>
      </div>
    `;
  }
}